'use client';

import React, { useMemo } from 'react';
import useGetAllPermissions from 'src/hooks/roles/useGetAvailablePermissions';
import { PermissionMatrix } from '../PermissionMatrix';

const RoleDisplay = ({
  role,
}: {
  role: MedScout.Role | null;
}): React.ReactElement => {
  const { data: allPermissions } = useGetAllPermissions();

  // The matrix shows every available permission, checked against the role.
  // Union the catalog with the role's own codes so a permission the role holds
  // never disappears, even if it isn't present in the catalog response.
  const structurePermissions = useMemo(() => {
    const catalogCodes = (allPermissions?.results ?? []).map(
      (permission) => permission.code
    );

    return Array.from(new Set([...catalogCodes, ...(role?.permissions ?? [])]));
  }, [allPermissions, role?.permissions]);

  // Catalog code -> description, surfaced as each row's helper text.
  const permissionDescriptions = useMemo(() => {
    const map: Record<string, string> = {};
    (allPermissions?.results ?? []).forEach((permission) => {
      if (permission.description) map[permission.code] = permission.description;
    });

    return map;
  }, [allPermissions]);

  return (
    <div
      className="flex flex-col flex-1 bg-white rounded-lg"
      data-testid="role-display"
    >
      {!role ? (
        <div className="flex flex-col gap-2 p-4" data-testid="no-role-message">
          <span className="text-xl text-primary">No role selected</span>
        </div>
      ) : (
        <PermissionMatrix
          permissions={structurePermissions}
          grantedPermissions={role.permissions ?? []}
          descriptions={permissionDescriptions}
        />
      )}
    </div>
  );
};

export default RoleDisplay;
